// ─── amazonAgent.js ───────────────────────────────────────────────────────────
// Drop-in replacement for simulateAgent() in data.js.
// Calls the Anthropic API with claude-haiku to simulate Amazon PA API responses,
// then normalizes the output to match the schema expected by computeResults():
//
//   { item, brand, price, rating, inStock, store }
//
// Usage in data.js — replace simulateAgent() with:
//
//   import { runAmazonAgent } from './agents/amazonAgent';
//
//   export function simulateAgent(store, query) {
//     if (store.id === 'amazon') return runAmazonAgent(store, query);
//     // keep mock for other stores until their agents are built
//     return simulateMockAgent(store, query);
//   }
// ─────────────────────────────────────────────────────────────────────────────

// Calls our local proxy server (server.js) which forwards to Anthropic.
// The API key stays on the server — never exposed to the browser.
const ANTHROPIC_API = '/api/anthropic';
const MODEL         = 'claude-haiku-4-5-20251001';

// ── 1. Build prompt ───────────────────────────────────────────────────────────
function buildPrompt(query) {
  return `Return ONLY a raw JSON object. No markdown, no explanation, no extra text.

Simulate an Amazon Product Advertising API search response for: "${query}"

Return exactly 3 products using this schema:
{
  "items": [
    {
      "title": "Full product title",
      "brand": "Brand name",
      "price": 49.99,
      "original_price": 69.99,
      "rating": 4.5,
      "review_count": 12453,
      "condition": "New",
      "seller_type": "FBA",
      "seller_name": "Amazon.com",
      "is_prime": true,
      "shipping_free": true,
      "shipping_cost": 0,
      "asin": "B0CH6XXXXX",
      "availability": "In Stock"
    }
  ]
}

Rules:
- REAL product names, brands, and realistic prices for "${query}"
- Mix seller_type: "FBA" for 2 items, "3P" for 1 item
- original_price higher than price for 2 items (on sale), same for 1
- is_prime: true for FBA items, false for 3P
- shipping_free: true when is_prime or price >= 35
- Vary ratings between 3.8 and 4.9, review counts 100–80000
- Use realistic B0 + 8 alphanumeric char ASINs
- Price the 3 items at low / mid / premium tiers for "${query}"`;
}

// ── 2. Call Anthropic API ─────────────────────────────────────────────────────
async function callAPI(prompt) {
  const response = await fetch(ANTHROPIC_API, {
    method:  'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: 2000,
      messages:   [{ role: 'user', content: prompt }],
    }),
  });

  if (!response.ok) {
    throw new Error(`Amazon agent API error: ${response.status} ${response.statusText}`);
  }

  const data    = await response.json();
  const rawText = data.content?.find(b => b.type === 'text')?.text || '';

  // Robust JSON extraction — handles any leading/trailing text
  const first = rawText.indexOf('{');
  const last  = rawText.lastIndexOf('}');
  if (first === -1 || last === -1) {
    throw new Error(`Amazon agent: no JSON found in response`);
  }

  return JSON.parse(rawText.slice(first, last + 1));
}

// ── 3. Normalize to data.js schema ───────────────────────────────────────────
// data.js computeResults() expects:
//   { item, brand, price, rating, inStock, store }
// We add extra Amazon-specific fields that ResultsTable can optionally display.
function normalizeItem(raw, store) {
  const price         = parseFloat(raw.price)          || 0;
  const originalPrice = parseFloat(raw.original_price) || null;
  const discountPct   = originalPrice && originalPrice > price
    ? Math.round((1 - price / originalPrice) * 100)
    : null;

  return {
    // ── Required by computeResults / ResultsTable ──
    item:     raw.title    || 'Unknown Product',
    brand:    raw.brand    || 'Unknown',
    price,
    rating:   parseFloat(raw.rating) || 4.0,
    inStock:  raw.availability !== 'Out of Stock',
    store:    store.id,             // 'amazon' — used by ArbitragePanel & badges

    // ── Amazon-specific extras (UI can use these for badges) ──
    asin:           raw.asin          || null,
    url:            raw.url           || `https://www.amazon.com/s?k=${encodeURIComponent(raw.title||'')}`,
    original_price: originalPrice,
    discount_pct:   discountPct,
    review_count:   parseInt(raw.review_count) || 0,
    condition:      raw.condition     || 'New',
    seller_type:    raw.seller_type   || 'FBA',      // "FBA" | "3P"
    seller_name:    raw.seller_name   || 'Amazon.com',
    is_prime:       raw.is_prime      === true,
    shipping_free:  raw.shipping_free === true,
    shipping_cost:  parseFloat(raw.shipping_cost) || 0,
    total_cost:     +(price + (raw.shipping_free ? 0 : (parseFloat(raw.shipping_cost) || 0))).toFixed(2),
    marketplace:    'amazon',
  };
}

// ── 4. Main export — matches simulateAgent(store, query) signature ────────────
export async function runAmazonAgent(store, query) {
  const prompt   = buildPrompt(query);
  const parsed   = await callAPI(prompt);
  const items    = Array.isArray(parsed.items) ? parsed.items : [];
  return items.map(item => normalizeItem(item, store));
}

// ── 5. Default export for convenience ────────────────────────────────────────
export default runAmazonAgent;
